import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { http, formatErr } from "../../lib/api";
import { fileToDataUrl, stripDataUrl, downloadB64 } from "../../lib/files";
import { usePolling } from "../../lib/usePolling";
import { FilterBanner } from "../../components/FilterBanner";
import { SignaturePad } from "../../components/SignaturePad";
import { dockind } from "../../lib/labels";
import { toast } from "sonner";
import { Download, PenLine, CheckCircle2, Upload } from "lucide-react";

export const DOC_STATUS = { da_firmare: "Da firmare", parzialmente_firmato: "Parzialmente firmato", da_verificare: "Copia firmata in verifica", da_correggere: "Correzione richiesta", completato: "Completato" };
const CLOSED = ["completato", "da_verificare"];
const DOC_ROLES = [{ value: "cliente", label: "Cliente (titolare salone)" }, { value: "agente", label: "Agente (raccoglie la firma)" }];

export default function AgentDocuments() {
  const [list, setList] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [padFor, setPadFor] = useState(null);
  const [uploadRole, setUploadRole] = useState({});
  const [sp, setSp] = useSearchParams();
  const onlyPending = sp.get("filter") === "pending";

  const load = async () => {
    try { const { data } = await http.get("/crm/me/documents"); setList(data); setBlocked(false); }
    catch (e) { if (e?.response?.status === 403) { setBlocked(true); setList([]); } else toast.error(formatErr(e)); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  usePolling(load, { interval: 10000, enabled: !blocked });

  const dl = async (id, which) => {
    try { const { data } = await http.get(`/crm/me/documents/${id}/download`, { params: { which } });
      downloadB64(data.data_b64, data.filename || "documento.pdf", data.content_type); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const sign = async (id, payload) => {
    try { await http.post(`/crm/me/documents/${id}/sign-device`, payload); setPadFor(null); await load(); window.dispatchEvent(new Event("glowbit:badges-refresh")); toast.success("Firma acquisita e documento aggiornato."); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const pending = (d) => (d.required_signers || []).filter((r) => !(d.signed_roles || []).includes(r));

  const uploadSigned = async (id, role, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Carica un PDF."); return; }
    try {
      const dataUrl = await fileToDataUrl(file);
      await http.post(`/crm/me/documents/${id}/sign-upload`, {
        signer_role: role, filename: file.name, data_b64: stripDataUrl(dataUrl), content_type: "application/pdf",
      });
      await load();
      window.dispatchEvent(new Event("glowbit:badges-refresh"));
      toast.success("Copia firmata caricata.");
    } catch (err) { toast.error(formatErr(err)); }
    e.target.value = "";
  };

  if (blocked) return <div data-testid="agent-documents-blocked" className="max-w-2xl mx-auto card-clean p-6 text-center"><h1 className="font-serif text-2xl mb-2">Documenti</h1><p className="text-sm text-neutral-600">Disponibili dopo l'attivazione del tuo account.</p></div>;
  if (list === null) return <div className="p-6 text-sm text-neutral-500">Caricamento…</div>;
  const shown = onlyPending ? list.filter((d) => !CLOSED.includes(d.status)) : list;

  return (
    <div data-testid="agent-documents-page" className="max-w-4xl mx-auto">
      <h1 className="font-serif text-3xl mb-1">Documenti da firmare</h1>
      <p className="text-sm text-neutral-500 mb-5">Consulta il documento completo prima di firmare. Puoi raccogliere la firma sul dispositivo (il firmatario firma di persona) oppure caricare la copia firmata.</p>
      <FilterBanner label={onlyPending ? "Da firmare" : null} onClear={() => setSp({})} />
      <div className="space-y-3" data-testid="agent-documents-list">
        {shown.length === 0 && <div className="card-clean p-6 text-sm text-neutral-500">Nessun documento{onlyPending ? " da firmare" : " assegnato"}.</div>}
        {shown.map((d) => {
          const roleOpts = DOC_ROLES.filter((r) => pending(d).includes(r.value));
          const upRole = uploadRole[d.id] || (roleOpts[0] && roleOpts[0].value);
          return (
          <div key={d.id} className="card-clean p-4" data-testid={`agent-document-${d.id}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex-1">
                <div className="font-semibold">{d.title} <span className="text-[11px] text-neutral-400">· {dockind(d.kind)}</span></div>
                <div className="text-xs text-neutral-500"><span data-testid={`agent-doc-status-${d.id}`}>Stato: {DOC_STATUS[d.status] || d.status}</span> · Firme richieste: {d.required_signers.join(", ")} · Firmate: {(d.signed_roles || []).join(", ") || "—"}</div>
                {d.correction_note && d.status === "da_correggere" && <div className="text-xs text-rose-700 mt-1" data-testid={`agent-doc-correction-${d.id}`}>Nota correzione: {d.correction_note}</div>}
              </div>
              <button onClick={() => dl(d.id, "original")} data-testid={`agent-doc-orig-${d.id}`} className="px-3 py-1.5 rounded-md border text-xs flex items-center gap-1" style={{ borderColor: "var(--border-silver)" }}><Download className="w-3 h-3" /> Originale</button>
              {(d.versions || []).some((v) => v.kind === "signed") && <button onClick={() => dl(d.id, "latest")} data-testid={`agent-doc-signed-${d.id}`} className="px-3 py-1.5 rounded-md border text-xs flex items-center gap-1" style={{ borderColor: "var(--border-silver)" }}><Download className="w-3 h-3" /> Firmato</button>}
              {!CLOSED.includes(d.status) && <button onClick={() => setPadFor(padFor === d.id ? null : d.id)} data-testid={`agent-doc-sign-${d.id}`} className="px-3 py-1.5 rounded-md text-white text-xs flex items-center gap-1" style={{ background: "var(--brand-dark)" }}><PenLine className="w-3 h-3" /> Firma sul dispositivo</button>}
              {d.status === "completato" && <span className="text-xs text-green-700 flex items-center gap-1" data-testid={`agent-doc-done-${d.id}`}><CheckCircle2 className="w-4 h-4" /> Completato</span>}
              {d.status === "da_verificare" && <span className="text-xs text-amber-700" data-testid={`agent-doc-review-${d.id}`}>In verifica dall'amministratore</span>}
            </div>
            {!CLOSED.includes(d.status) && roleOpts.length > 0 && (
              <div className="flex items-center gap-2 mt-3 flex-wrap" data-testid={`agent-doc-upload-${d.id}`}>
                <span className="text-xs text-neutral-500">Alternativa manuale:</span>
                <select value={upRole} onChange={(e) => setUploadRole((s) => ({ ...s, [d.id]: e.target.value }))} data-testid={`agent-doc-upload-role-${d.id}`} className="px-2 py-1 rounded-md border text-xs" style={{ borderColor: "var(--border-silver)" }}>
                  {roleOpts.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
                <label className="px-3 py-1.5 rounded-md border text-xs flex items-center gap-1 cursor-pointer" style={{ borderColor: "var(--border-silver)" }} data-testid={`agent-doc-upload-label-${d.id}`}>
                  <Upload className="w-3 h-3" /> Scarica e carica firmato
                  <input type="file" accept="application/pdf" className="hidden" onChange={(e) => uploadSigned(d.id, upRole, e)} data-testid={`agent-doc-upload-input-${d.id}`} />
                </label>
              </div>
            )}
            {padFor === d.id && (
              <div className="border rounded-lg p-4 mt-3" style={{ borderColor: "var(--border-silver)" }}>
                <p className="text-xs text-neutral-500 mb-2">Seleziona il firmatario e fai firmare la persona indicata, poi conferma.</p>
                <SignaturePad roles={roleOpts} onConfirm={(payload) => sign(d.id, payload)} />
              </div>
            )}
          </div>
        );})}
      </div>
    </div>
  );
}
